import { createDefaultGrid } from '@sites/entity-core';
import type { NormalizedGridEntity } from '@web/types';

import { getUuid } from '@common/helpers';

export const gridInstance = ({
    parentId,
    children,
    variant = 0,
}: Partial<Pick<NormalizedGridEntity, 'parentId' | 'children'>> & { variant: number }): NormalizedGridEntity => {
    const defaultGrid = createDefaultGrid();

    const childrenValue = children ?? setInitialChildren(variant);

    defaultGrid.props.rowCellWidth.desktop = setInitialRowCellWidth(variant);

    return {
        ...defaultGrid,
        props: {
            ...defaultGrid.props,
            variant,
        },
        id: getUuid(),
        parentId,
        children: childrenValue,
    };
};

export type VariantLayout = { colSpan: number; index?: number }[][];
export type Variant = { layout: VariantLayout; hidden?: boolean };
const GRID_VARIANTS_MAP: Record<number, Partial<Variant>> = {
    0: { layout: [[{ colSpan: 4 }, { colSpan: 4 }]] },
    1: { layout: [[{ colSpan: 4 }, { colSpan: 4 }], [{ colSpan: 8 }]] },
    2: { layout: [[{ colSpan: 3 }, { colSpan: 5 }]] },
    3: { layout: [[{ colSpan: 2 }, { colSpan: 4 }, { colSpan: 2 }]] },
    4: {
        layout: [
            [{ colSpan: 4 }, { colSpan: 4 }],
            [{ colSpan: 4 }, { colSpan: 4 }],
        ],
    },
    5: { layout: [[{ colSpan: 8 }], [{ colSpan: 4 }, { colSpan: 4 }]] },
    6: { layout: [[{ colSpan: 8 }]], hidden: true },
};

const VARIANTS_LENGTH_MAP = new Map<number, number>(
    Object.entries(GRID_VARIANTS_MAP).map(([k, v]) => [Number(k), v.layout.flat().length])
);

export function getGridVariants() {
    return GRID_VARIANTS_MAP;
}

export function getVariantMapList(variant: number): number {
    return VARIANTS_LENGTH_MAP.get(variant) ?? VARIANTS_LENGTH_MAP.get(0);
}

function getVariantLayout(variant: number) {
    const variants = getGridVariants();
    if (variants[variant]) {
        return prepareVariantLayout(variants[variant].layout);
    }

    return prepareVariantLayout(variants[0].layout);
}
function prepareVariantLayout(layout: VariantLayout): VariantLayout {
    let index = 0;
    return layout.map((row) => {
        return row.map((cell) => {
            return {
                colSpan: cell.colSpan,
                index: index++,
            };
        });
    });
}
export function setInitialRowCellWidth(variant: number): number[][] {
    return getVariantLayout(variant).map((row) => {
        return row.map((cell) => {
            return cell.colSpan;
        });
    });
}
export function setInitialChildren(variant: number): string[][] {
    return getVariantLayout(variant).map(() => {
        return [];
    });
}
